import { FC, useEffect, useContext, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  phoenixPrearrivalGetCustomerPreferenceFeatures,
  phoenixPrearrivalUpdateCustomerPreferenceFeatures,
} from '../../graphql';
import { StyledThankYouContainer } from './ThankyouDetail.styles';
import { ThankyouDetailProps } from './ThankyouDetail.types';
import { SurveyWrapper } from '../../molecules/SurveyWrapper';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Button, Heading, Image, Text, Types, useCheckBreakpoint } from '@marriott/mi-ui-library';
import dayjs from 'dayjs';
import { useCreateSurveyStore } from '../../store/createSurveyStore';
import { brandColorSchemeGenerator } from '../../util/constants';
import { updateDatalayer } from '../../util/dataLayer/datalayerUtil';
import DOMPurify from 'isomorphic-dompurify';
import moment from 'moment';
import { SurveyWrapperInner } from '../../molecules/SurveyWrapperInner';
import { capitalize, generateApolloClientHeaders } from '../../util/helpers';
import { PageContext } from '../../context';
import { usePersistentGlobalStore } from '@marriott/mi-store-utils';
import { MBTACallout } from '../../molecules/MBTACallout';
import { fetchMBTAcalloutData, prefetchMBTAcalloutData } from '../../api/mbtaCalloutService';
import { MBTAExperience } from '../../molecules/MBTACallout/MBTACallout.types';
import { getResConfirmationNumberValue } from '../../util/dataLayer/resConfirmationNumberUtil';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ThankyouDetail: FC<ThankyouDetailProps> = (props: any) => {
  const { formData, form_prearrival_questions_complete, setFormData } = useCreateSurveyStore(); // Access formData from Zustand store
  const [arrivalDate, setArrivalDate] = useState(moment().format('MM/DD/YYYY'));
  const [depatureDate, setDepatureDate] = useState(moment().format('MM/DD/YYYY'));
  const descriptionText = props?.model?.description;
  const marshaCode = formData?.property_config?.marsha_cd;
  const alternateCID =
    formData?.reservation?.AlternateCustomerId || props?.reservation?.AlternateCustomerId || formData?.guest_acid;
  const confirmationNumber = formData?.reservation?.confirmationNumber || props?.reservation?.confirmationNumber;
  const confoCreate = formData?.reservation?.confoCreate || props?.reservation?.confoCreate;
  const [thankYouDetailText, setThankYouDetailText] = useState(props?.model?.mainHeading);
  const combinedValue = getResConfirmationNumberValue(confoCreate, confirmationNumber);

  const isMobileViewPort = useCheckBreakpoint('viewportS');
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const globalData = usePersistentGlobalStore((state: any) => state.globalData);
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const [globalSessionData, setGlobalSessionData] = useState<any>(null);
  const handleNextPage = () => {
    const rawUrl = (
      formData?.property_config?.hotel_property_url ||
      formData?.property_config?.brand_homepage_url ||
      ''
    ).trim();

    if (!rawUrl) {
      return;
    }

    const pathName = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
    window.open(pathName, '_blank');
  };

  const sanitizedNode1 = DOMPurify.sanitize(descriptionText, {
    RETURN_DOM: true,
    ALLOWED_ATTR: ['href', 'target', 'class'],
    ALLOWED_TAGS: ['a', 'p'],
  }) as HTMLElement;
  const fragmentString1 = sanitizedNode1.innerHTML;

  const [brandCdSetPage, setBrandCdSetPage] = useState('');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [surveyThankyouTitle, setSurveyThankyouTitle] = useState('');

  const [customerFirstName, setCustomerFirstName] = useState('');
  const [customerLastName, setCustomerLastName] = useState('');

  const [hotelName, setHotelName] = useState('');
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const i18nText: any = props?.i18nMap;
  const caTitle = props?.model?.caTitle ?? '';
  const caDescription = props?.model?.caDescription ?? '';
  const caImage = props?.model?.caImage ?? '';
  const caCTALabel = props?.model?.caCTALabel ?? '';
  const caCTALinkTemplate = props?.model?.caCTALink ?? '';
  const normalizedMarshaCode = typeof marshaCode === 'string' ? marshaCode.trim() : '';
  const validatedMarshaCode = /^[A-Za-z0-9_-]+$/.test(normalizedMarshaCode) ? normalizedMarshaCode : '';
  const encodedMarshaCode = encodeURIComponent(validatedMarshaCode);
  const joinMarriottBonvoyUrl = `https://www.joinmarriottbonvoy.com/${encodedMarshaCode}`;
  // Replace placeholders in the base URL
  const createAccountLink = caCTALinkTemplate
    .replace('{marshaCode}', encodedMarshaCode)
    .replace('{startDate}', `${arrivalDate ? dayjs(arrivalDate).format('YYYYMMDD') : ''}`)
    .replace('{endDate}', `${depatureDate ? dayjs(depatureDate).format('YYYYMMDD') : ''}`)
    .replace('{acid}', `${alternateCID ? alternateCID : ''}`);
  const afterSurveyWrapperInnerReplace: string = props?.model?.afterSuveryWrapperInner?.replace(
    '{marshaCode}',
    marshaCode
  );
  const [enableMBTACallout, setEnableMBTACallout] = useState<boolean>(props?.model?.setEnableMBTACallout ?? false);
  const [brandCodeProps, setBrandCodeProps] = useState('');
  const [prefetchedMbtaData, setPrefetchedMbtaData] = useState<MBTAExperience[]>([]);

  useEffect(() => {
    const marshaCd = formData?.property_config?.marsha_cd;
    const arrival = formData?.reservation?.arrival?.replaceAll('-', '');
    const departure = formData?.reservation?.departure?.replaceAll('-', '');

    if (!enableMBTACallout || !marshaCd || !arrival || !departure) {
      return;
    }

    const requestData = {
      scope: 'full',
      code: marshaCd,
      currency: formData?.reservation?.currency || 'USD',
      from: arrival,
      to: departure,
    };

    let isMounted = true;

    // Warm cache immediately, then hydrate state from cached/network response.
    prefetchMBTAcalloutData(requestData);
    fetchMBTAcalloutData(requestData).then(mbtaData => {
      if (!isMounted) {
        return;
      }
      setPrefetchedMbtaData(Array.isArray(mbtaData?.response) ? mbtaData.response : []);
    });

    return () => {
      isMounted = false;
    };
  }, [
    enableMBTACallout,
    formData?.property_config?.marsha_cd,
    formData?.reservation?.arrival,
    formData?.reservation?.departure,
    formData?.reservation?.currency,
  ]);

  useEffect(() => {
    const modelShowMBTACallout = props?.model?.setEnableMBTACallout;

    setEnableMBTACallout(modelShowMBTACallout !== undefined ? modelShowMBTACallout : false);
    setArrivalDate(dayjs(formData?.reservation?.arrival).format('MM/DD/YYYY'));
    setDepatureDate(dayjs(formData?.reservation?.departure).format('MM/DD/YYYY'));
    setHotelName(formData?.property_config?.hotel_name);
    setCustomerLastName(capitalize(formData?.reservation?.customerLastName));
    setCustomerFirstName(capitalize(formData?.reservation?.customerFirstName));
    setThankYouDetailText(formData?.property_config?.survey_thank_you_title ?? props?.model?.mainHeading);
    setSurveyThankyouTitle(thankYouDetailText !== '' ? thankYouDetailText : props?.model?.mainHeading);
    if (props?.brandCode) {
      if (props?.property_config?.non_branded_flg === 'Y') {
        setBrandCdSetPage(brandColorSchemeGenerator('NBF'));
        setBrandCodeProps('NBF');
      } else {
        setBrandCdSetPage(brandColorSchemeGenerator(props?.brandCode));
        setBrandCodeProps(props?.brandCode);
      }
    } else {
      if (formData?.property_config?.non_branded_flg === 'Y') {
        setBrandCdSetPage(brandColorSchemeGenerator('NBF'));
        setBrandCodeProps('NBF');
      } else {
        setBrandCdSetPage(brandColorSchemeGenerator(formData?.property_config?.brand_cd || formData?.brandCode));
        setBrandCodeProps(props?.brandCode);
      }
    }
    if (props?.backgroundImage) {
      setBackgroundImageUrl(props?.backgroundImage);
    } else {
      setBackgroundImageUrl(formData?.property_config?.background_image_url);
    }
    updateDatalayer('form_prearrival_questions_complete', form_prearrival_questions_complete);
  }, [
    formData?.reservation?.arrival,
    formData?.property_config?.hotel_name,
    formData?.reservation?.customerLastName,
    formData?.reservation?.customerFirstName,
    formData?.property_config?.survey_thank_you_title,
    props?.brandCode,
    formData?.property_config?.brand_cd,
    props?.backgroundImage,
    formData?.property_config?.background_image_url,
    props?.model?.setEnableMBTACallout,
    props?.model?.mainHeading,
    form_prearrival_questions_complete,
  ]);

  // CONSENT CODE START HERE
  const [saveCustomerFeaturesPayload, setSavedCustomerFeaturesPayload] = useState([]);
  const { IS_LOCAL_DEV } = process.env;
  const pageContext = useContext(PageContext);

  // init the session data
  useEffect(() => {
    if (globalData !== undefined && globalData != null) {
      setGlobalSessionData(globalData);
    }
  }, [globalData]);
  const sessionData = globalSessionData?.sessionData?.cacheData?.data || pageContext?.sessionData?.cacheData?.data;

  const EMEA_OPTED_OUT_PERSONALIZATION_KEY = 'OPTED_OUT_PERSONALIZATION';
  const [emeaConsent, setEmeaConsent] = useState<
    Array<{
      type: string;
      value: boolean;
    }>
  >([
    {
      type: 'OPTED_OUT_PERSONALIZATION',
      value: true,
    },
  ]);

  // Determine if we should fetch user preferences (only after user is signed in)
  // fetch trigger condition
  const [shouldFetch, setShouldFetch] = useState(false);
  useEffect(() => {
    setShouldFetch(
      !!sessionData?.consumerID &&
        formData?.isUserEnableSignIn &&
        !formData?.isRememberUser &&
        formData?.consentCheckbox &&
        !formData?.isSaveConsentCall
    );
  }, [sessionData?.consumerID, formData?.isUserEnableSignIn, formData?.isRememberUser]);
  const { data } = useQuery(phoenixPrearrivalGetCustomerPreferenceFeatures, {
    variables: {
      customerId: sessionData?.consumerID,
    },
    context: generateApolloClientHeaders(IS_LOCAL_DEV === 'true', pageContext),
    skip: !shouldFetch,
  });

  useEffect(() => {
    // Process data only after query sucess
    const features =
      data?.customer?.featuresByGroup?.preferencesFeatures?.filter(
        (f: any) => f?.feature?.enumCode === EMEA_OPTED_OUT_PERSONALIZATION_KEY
      ) || [];

    setSavedCustomerFeaturesPayload(
      features.map((f: any) => ({
        code: f.feature.enumCode,
        value: f.value,
      }))
    );

    const updatedEmeaConsent = features.map((code: any) => ({
      type: code.feature.enumCode,
      value: code.value === 'N' ? true : false,
    }));

    // update form data with user's consent state

    setEmeaConsent([...updatedEmeaConsent]);
  }, [data]);

  useEffect(() => {
    // normal user work fine
    // signed user - already consented - emeaConsent[0]?.value === false
    // isSaveConsentCall - when saveConsent already called when thankyou page load twice

    if (emeaConsent != undefined && emeaConsent[0]?.value === false && !formData?.isSaveConsentCall) {
      saveConsent();
    }
  }, [emeaConsent]);

  const updateConsentsPayload = (consentsData: Array<{ type: string; value: boolean }>) => {
    const updatedFeatures = consentsData
      .filter(item => item.type === EMEA_OPTED_OUT_PERSONALIZATION_KEY)
      .map(item => ({
        code: item.type,
        value: item.value ? 'N' : 'Y',
      }));

    if (!updatedFeatures.length) {
      return { updatedFeatures: saveCustomerFeaturesPayload };
    }
    return { updatedFeatures };
  };

  const [updateCustomerConsents] = useMutation(phoenixPrearrivalUpdateCustomerPreferenceFeatures);

  // Function to save user consent to backend
  const saveConsent = () => {
    const { updatedFeatures } = updateConsentsPayload(emeaConsent);
    console.log(updatedFeatures);
    const customerFeaturesInput = {
      customerFeatures: [
        {
          code: 'OPTED_OUT_PERSONALIZATION',
          value: 'N',
        },
      ],
      customerId: sessionData?.consumerID,
    };

    updateCustomerConsents({
      variables: {
        customerFeaturesInput,
      },
      context: generateApolloClientHeaders(IS_LOCAL_DEV === 'true', pageContext),
      onCompleted: (data: any) => {
        const features =
          data?.customer?.featuresByGroup?.preferencesFeatures?.filter(
            (f: any) => f?.feature?.enumCode === EMEA_OPTED_OUT_PERSONALIZATION_KEY
          ) || [];

        const updatedEmeaConsent = features.map((code: any) => ({
          type: code.feature.enumCode,
          value: code.value === 'N' ? true : false,
        }));

        // update global form data after save
        setFormData({
          ...formData,
          isUserConsentChecked: updatedEmeaConsent[0]?.value,
          isSubmitDataFlow: false,
          isReviewPageFlow: false,
          isSaveConsentCall: true,
        });
      },
      onError: () => {},
    });
  };

  useEffect(() => {
    // user already signed in - isUserEnableSignIn
    // form is already submitted - isTPFAlreadySubmitted
    // called when submit button click - isSubmitDataFlow
    // Ensure sessionData has the latest consumerID before calling GraphQL
    // Trigger when user logs in or form flow starts

    if (
      (formData?.isUserEnableSignIn || formData?.isMFAActive) &&
      formData?.isSubmitDataFlow &&
      formData?.consentCheckbox &&
      !formData?.isSaveConsentCall &&
      !formData?.isRememberUser &&
      !formData?.isUserConsentChecked &&
      emeaConsent != undefined &&
      emeaConsent[0]?.value === false
    ) {
      (async () => {
        const consumerId = sessionData?.consumerID;

        if (consumerId) {
          try {
            // Wait slightly for Apollo cache to stabilize
            setTimeout(() => {
              saveConsent();
            }, 300);
          } catch (err) {
            console.error('Refetch failed after waiting for consumerId:', err);
          }
        } else {
          console.warn('Timed out waiting for sessionData.consumerID');
        }
      })();
    }

    if (formData?.isSubmitDataFlow) {
      setFormData({
        ...formData,
        isThankYouPageLoad: true,
      });
    }
  }, [
    formData?.isUserEnableSignIn,
    formData?.isSubmitDataFlow,
    formData?.isReviewPageFlow,
    sessionData?.consumerID, // <— important to watch for session update
  ]);
  return (
    <EditableComponent {...props}>
      <>
        <SurveyWrapper
          marshaCode={marshaCode}
          backgroundImage={backgroundImageUrl}
          className="brand-css common-color-set"
          colorScheme={brandCdSetPage}
          alternateId={alternateCID}
          productInfo={marshaCode}
          formPageName="Thank you"
          formName="Prearrival Travel Form"
          prearrival_form_amenities_added_to_plan={formData.prearrivalformamenitiesaddedtoplan}
          prearrival_form_booking_confirmation_id={combinedValue}
          env_api_tile_error_info=""
          form_prearrival_questions_complete={form_prearrival_questions_complete}
          beforeSuveryWrapperOuter={props?.model?.beforeSuveryWrapperOuter}
          afterSuveryWrapperOuter={props?.model?.afterSuveryWrapperOuter}
        >
          <StyledThankYouContainer data-testid="thank-you-container" data-component-name="o-account-thankYouContainer">
            <SurveyWrapperInner
              beforeSuveryWrapperInner={props?.model?.beforeSuveryWrapperInner}
              afterSuveryWrapperInner={afterSurveyWrapperInnerReplace}
            >
              <div className="thankYouPage">
                <div className="container">
                  <div className="row">
                    <div className="col-12">
                      <div className={`thankyou-Wrapper`}>
                        <h1 className="mb-3">
                          {surveyThankyouTitle && customerFirstName && customerLastName ? (
                            <>
                              {surveyThankyouTitle ? `${surveyThankyouTitle},` : ''}
                              <b>
                                {isMobileViewPort && <>&nbsp;</>}
                                {customerFirstName} {customerLastName}!
                              </b>
                            </>
                          ) : (
                            <>{`${surveyThankyouTitle}!`}</>
                          )}
                        </h1>
                        <p className="mb-3 t-font-m">
                          We look forward to welcoming you at{' '}
                          <span className="bold">
                            <b>{hotelName}</b>
                          </span>
                          <span> {props?.i18nMap?.on ?? 'on'} </span>
                          <span className="bold">
                            <b>{arrivalDate}. </b>
                          </span>
                          {props?.i18nMap?.emailConfirmationA ?? 'A copy of your Travel plan has been emailed to you.'}
                        </p>
                        <Text
                          element={Types.tags.div}
                          fontSize={Types.size.medium}
                          dangerouslySetInnerHTML={{
                            __html: fragmentString1.replace(
                              /href="[^"]*createAccountPage1\.mi"|Privacy Center/g,
                              match =>
                                match === 'Privacy Center'
                                  ? `<a href="https://www.marriott.com/about/privacy.mi" target="_blank">Privacy Center</a>`
                                  : `href="${joinMarriottBonvoyUrl}"`
                            ),
                          }}
                          data-testid="ThankYou Description"
                          customClass="mb-2 privacytext"
                        />
                        {(formData?.property_config?.hotel_property_url?.trim() ||
                          formData?.property_config?.brand_homepage_url?.trim()) && (
                          <div className="d-flex justify-content-center align-items-center">
                            <Button
                              className="m-button-m m-button-primary p-2 begin-button custom-btn mx-0 justify-content-center align-items-center flex-column px-2 py-3"
                              buttonCopy={props?.i18nMap?.visitHotelWebsite ?? 'Visit Hotel Website'}
                              callback={handleNextPage}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {(caImage || caTitle || caDescription || caCTALabel || caCTALinkTemplate) && (
                <div
                  data-component-name="a-ui-library-Text"
                  data-testid="ui-library-Text"
                  className="t-font-m box-section-model m-0"
                >
                  <div className="container-fluid container-custom d-flex flex-md-row flex-column">
                    <div className="col-md-2 p-0 position-relative left-image-container">
                      <Image
                        loading="lazy"
                        defaultImageURL={caImage}
                        renditions={[
                          {
                            renditionPath: caImage,
                            mediaValue: '576px',
                            dynamic: true,
                            damPath: caImage,
                            mediaQuery: '(max-width: 767px)',
                            height: 100,
                          },
                        ]}
                        altText={caTitle}
                        customClass="left-image-ab"
                      />
                    </div>
                    <div className="col-md-10 right-content-ab">
                      <Heading
                        variation={Types.headingType.title}
                        fontSize={Types.size.large}
                        element={Types.tags.h2}
                        titleText={caTitle}
                      />
                      <Text
                        element={Types.tags.div}
                        fontSize={Types.size.medium}
                        copyText={caDescription}
                        customClass="mb-3 description"
                        data-testid="UpdateDescription"
                      />
                      <Button
                        className="m-button-m m-button-primary cta-button-ab custom_click_track"
                        aria-label="Find Local Experiences"
                        data-is-co-brand="false"
                        isLink={true}
                        buttonCopy={caCTALabel}
                        href={createAccountLink}
                        custom_click_track_value="Thank You Callout|external"
                        target="_blank"
                      />
                    </div>
                  </div>
                </div>
              )}
            </SurveyWrapperInner>
          </StyledThankYouContainer>
          {formData && enableMBTACallout != false && (
            <MBTACallout
              marshaCode={formData?.property_config?.marsha_cd}
              resCurrency={formData?.reservation?.currency}
              startDate={formData?.reservation?.arrival}
              endDate={formData?.reservation?.departure}
              formDataHotelLocation={formData?.property_config?.hotel_location}
              brandCodeProps={brandCodeProps}
              colorScheme={brandCdSetPage}
              prefetchedMbtaData={prefetchedMbtaData}
            />
          )}
        </SurveyWrapper>
      </>
    </EditableComponent>
  );
};
